'use client';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

import {
  MARKETING_NAV_LINKS,
  type NavLink,
  type NavMenuItem,
} from '../config/marketing-nav-links';

function NavHref({
  item,
  className,
  children,
}: {
  item: NavLink | NavMenuItem;
  className?: string;
  children: React.ReactNode;
}) {
  if (item.external || item.href.startsWith('http') || item.href.startsWith('mailto:')) {
    return (
      <a
        href={item.href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link href={item.href} className={className}>
      {children}
    </Link>
  );
}

function MegaMenuItem({ item }: { item: NavMenuItem }) {
  return (
    <li>
      <NavHref item={item}>
        <div className="icon-box">
          {item.iconSrc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={item.iconSrc}
              alt={item.iconAlt || ''}
              width={24}
              height={24}
            />
          ) : null}
        </div>
        <div className="tool-info">
          <h4>{item.label}</h4>
          {item.description ? <p>{item.description}</p> : null}
        </div>
      </NavHref>
    </li>
  );
}

export function MarketingNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMegaMenu, setOpenMegaMenu] = useState<'resume' | 'resources' | null>(
    null,
  );

  const closeMenus = useCallback(() => {
    setMobileOpen(false);
    setOpenMegaMenu(null);
  }, []);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      const navbar = document.getElementById('navbar');
      if (navbar && !navbar.contains(target)) {
        setMobileOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenus();
      }
    };

    const onScroll = () => {
      const navbar = document.getElementById('navbar');
      if (!navbar) {
        return;
      }
      if (window.scrollY > 0) {
        navbar.classList.add('sticky');
        document.body.classList.add('sticky-navbar');
      } else {
        navbar.classList.remove('sticky');
        document.body.classList.remove('sticky-navbar');
      }
    };

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll);
      // Soft-nav must not leave sticky body styles on login/account.
      document.body.classList.remove('sticky-navbar');
      const navbar = document.getElementById('navbar');
      if (navbar) {
        navbar.classList.remove('sticky');
      }
    };
  }, [closeMenus]);

  const toggleMegaMenu = (menu: 'resume' | 'resources') => {
    setOpenMegaMenu((current) => (current === menu ? null : menu));
  };

  const resourceColumns = [
    MARKETING_NAV_LINKS.resources.slice(0, 4),
    MARKETING_NAV_LINKS.resources.slice(4),
  ];

  return (
    <>
      <nav id="navbar" className="navbar navbar-default">
        <div className="container container_kudos_pro main_logo_pro">
          <div className="navbar-header">
            <button
              type="button"
              className={`navbar-toggle collapsed${mobileOpen ? '' : ''}`}
              aria-expanded={mobileOpen}
              onClick={(event) => {
                event.stopPropagation();
                setMobileOpen((open) => !open);
              }}
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="material-icons menu-icon">menu</span>
            </button>
            <Link className="navbar-brand" href={MARKETING_NAV_LINKS.brand.href}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="top-logo hidden-xs"
                alt="KudosWall Pro Logo"
                src="/common-assets/images/pro-logo-teal.png"
                height={45}
                width={180}
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className="top-logo-mobile hidden-lg hidden-md hidden-sm"
                alt="KudosWall Pro Logo"
                src="/common-assets/images/pro-logo-teal.png"
                height={40}
                width={160}
              />
            </Link>
          </div>

          <div
            className={`collapse navbar-collapse${mobileOpen ? ' in open' : ''}`}
            id="navbarNav"
          >
            <ul className="nav navbar-nav navbar-right">
              <li className={`dropdown${openMegaMenu === 'resume' ? ' open' : ''}`}>
                <a
                  href="#"
                  className="dropdown-toggle"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={openMegaMenu === 'resume'}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleMegaMenu('resume');
                  }}
                >
                  Resume <span className="caret" />
                </a>
                <ul
                  className={`dropdown-menu mega-menu${openMegaMenu === 'resume' ? ' show' : ''}`}
                >
                  <div className="container mega-dropdown-menu">
                    <div className="row menu-tabs">
                      <div className="col-sm-6 resume-tabs">
                        <ul className="list-unstyled">
                          {MARKETING_NAV_LINKS.resume.map((item) => (
                            <MegaMenuItem key={item.href} item={item} />
                          ))}
                        </ul>
                      </div>
                      <div className="col-sm-6 menu-img-col">
                        <ul className="list-unstyled text-center menu-img">
                          <li>
                            <span>
                              Build professional resumes effortlessly with AI. Analyze
                              and optimize your resume for better job opportunities!
                            </span>
                          </li>
                          <li className="img-li">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src="/common-assets/images/megamenu-image.svg"
                              alt=""
                              className="img-responsive"
                            />
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </ul>
              </li>

              {MARKETING_NAV_LINKS.topLevel.map((item) => (
                <li key={item.href}>
                  <NavHref item={item}>{item.label}</NavHref>
                </li>
              ))}

              <li
                className={`dropdown${openMegaMenu === 'resources' ? ' open' : ''}`}
              >
                <a
                  href="#"
                  className="dropdown-toggle"
                  role="button"
                  aria-haspopup="true"
                  aria-expanded={openMegaMenu === 'resources'}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleMegaMenu('resources');
                  }}
                >
                  Resources <span className="caret" />
                </a>
                <ul
                  className={`dropdown-menu mega-menu${openMegaMenu === 'resources' ? ' show' : ''}`}
                >
                  <div className="container mega-dropdown-menu">
                    <div className="row menu-tabs">
                      <div className="col-sm-6 resume-tabs resources-tabs">
                        {resourceColumns.map((column, columnIndex) => (
                          <ul key={columnIndex} className="list-unstyled">
                            {column.map((item) => (
                              <MegaMenuItem key={item.href} item={item} />
                            ))}
                          </ul>
                        ))}
                      </div>
                      <div className="col-sm-6 menu-img-col">
                        <ul className="list-unstyled text-center menu-img">
                          <li>
                            <span>
                              Get expert resume tips, templates, and AI-powered tools to
                              land your dream job.
                            </span>
                          </li>
                          <li className="img-li">
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                              src="/common-assets/images/megamenu-image.svg"
                              alt=""
                              className="img-responsive"
                            />
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </ul>
              </li>

              <li>
                <Link href={MARKETING_NAV_LINKS.signIn.href}>
                  {MARKETING_NAV_LINKS.signIn.label}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <div
        className="mega-menu-overlay"
        style={{ display: openMegaMenu ? 'block' : 'none' }}
        onClick={closeMenus}
        aria-hidden={!openMegaMenu}
      />
    </>
  );
}
